import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '@/services/api-client';
import {
  BookOpen,
  Brain,
  FileText,
  Download,
  Share2,
  Sparkles,
  Filter,
  Search,
  Eye,
  Edit,
  Trash2,
  Plus,
  Link as LinkIcon,
  Tag,
  Clock,
  TrendingUp,
  Layers,
  Target,
  Zap,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
  RefreshCw,
  Globe,
  BookmarkPlus,
  List,
  Grid,
  SlidersHorizontal,
  CheckSquare,
  Square,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { ResearchManuscript } from '@/components/apps/ResearchManuscript';
import { tabService, ApiBrowserTab } from '@/services/tabs';
import { api } from '@/services/client';

interface Source {
  id: string;
  url: string;
  title: string;
  domain: string;
  favicon?: string;
  visitedAt: Date;
  excerpts: string[];
  relevance: number;
  tags: string[];
}

interface ResearchTopic {
  id: string;
  name: string;
  description: string;
  sources: Source[];
  createdAt: Date;
  lastUpdated: Date;
  synthesisStatus: 'pending' | 'processing' | 'completed';
}

interface SynthesisSection {
  id: string;
  title: string;
  content: string;
  sources: string[];
  insights: string[];
  expanded: boolean;
}

interface Synthesis {
  id: string;
  topicId: string;
  title: string;
  abstract: string;
  sections: SynthesisSection[];
  keyFindings: string[];
  recommendations: string[];
  createdAt: Date;
  wordCount: number;
  appId?: string;
  journalFormat?: string;
  manuscriptMarkdown?: string;
  usedLlm?: boolean;
  status?: string;
  sources?: Source[];
}

type ViewMode = 'topics' | 'synthesis' | 'sources';

const JOURNAL_FORMATS = ['ieee', 'apa', 'nature', 'mla', 'chicago', 'acm', 'vancouver', 'science'] as const;

function normalizeTopic(raw: any): ResearchTopic {
  const sources = (raw.sources || []).map((s: any) => ({
    ...s,
    visitedAt: s.visitedAt ? new Date(s.visitedAt) : new Date(),
    relevance: typeof s.relevance === 'number' && s.relevance <= 1 ? s.relevance * 100 : (s.relevance ?? 50),
  }));
  return {
    id: raw.id,
    name: raw.name || 'Untitled topic',
    description: raw.description || '',
    sources,
    createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
    lastUpdated: raw.lastUpdated ? new Date(raw.lastUpdated) : new Date(),
    synthesisStatus: raw.synthesisStatus || 'pending',
  };
}

function normalizeSynthesis(raw: any, topicId: string): Synthesis {
  return {
    id: raw.id,
    topicId: raw.topicId || raw.topic_id || topicId,
    title: raw.title || 'Research synthesis',
    abstract: raw.abstract || raw.summary || '',
    sections: (raw.sections || []).map((s: any) => ({
      id: s.id || crypto.randomUUID(),
      title: s.title || 'Section',
      content: s.content || '',
      sources: s.sources || [],
      insights: s.insights || [],
      expanded: Boolean(s.expanded),
    })),
    keyFindings: raw.keyFindings || raw.key_findings || [],
    recommendations: raw.recommendations || [],
    createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
    wordCount: raw.wordCount || raw.word_count || 0,
    appId: raw.app_id || raw.appId,
    journalFormat: raw.journal_format || raw.journalFormat,
    manuscriptMarkdown: raw.manuscript_markdown || raw.manuscriptMarkdown,
    usedLlm: Boolean(raw.used_llm ?? raw.usedLlm),
    status: raw.status,
    sources: (raw.sources || []).map((s: any) => ({
      ...s,
      visitedAt: s.visitedAt ? new Date(s.visitedAt) : new Date(),
      relevance: typeof s.relevance === 'number' && s.relevance <= 1 ? s.relevance * 100 : (s.relevance ?? 50),
    })),
  };
}

export function ResearchSynthesis() {
  const [topics, setTopics] = useState<ResearchTopic[]>([]);
  const [syntheses, setSyntheses] = useState<Synthesis[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<ResearchTopic | null>(null);
  const [activeSynthesis, setActiveSynthesis] = useState<Synthesis | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('topics');
  const [isGenerating, setIsGenerating] = useState(false);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [journalFormat, setJournalFormat] = useState<string>('ieee');
  const [exporting, setExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [topicName, setTopicName] = useState('Current session research');
  const [availableTabs, setAvailableTabs] = useState<ApiBrowserTab[]>([]);
  const [selectedTabIds, setSelectedTabIds] = useState<string[]>([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  useEffect(() => {
    loadResearchData();
  }, []);

  const loadWorkspaceTabs = async () => {
    setLoadingTabs(true);
    try {
      let sid = sessionStorage.getItem('khiimori_workspace_session_id');
      if (!sid) {
        try {
          const cur = await api.get<{ session: { id: string } }>('/sessions/current');
          sid = cur.data.session.id;
        } catch {
          const list = await api.get<{ sessions: { id: string }[] }>('/sessions');
          sid = list.data.sessions?.[0]?.id || null;
        }
      }
      if (sid) {
        sessionStorage.setItem('khiimori_workspace_session_id', sid);
        setActiveSessionId(sid);
        const { tabs } = await tabService.getTabsBySession(sid, 1, 200);
        setAvailableTabs(tabs);
        // By default, select all available tabs so user can uncheck or refine
        setSelectedTabIds(tabs.map(t => t.id));
      }
    } catch (e) {
      console.warn('Failed to load session tabs for research paper:', e);
    } finally {
      setLoadingTabs(false);
    }
  };

  const handleOpenNewTopicModal = () => {
    setShowNewTopicModal(true);
    void loadWorkspaceTabs();
  };

  const handleToggleTab = (id: string) => {
    setSelectedTabIds(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleSelectAllTabs = () => {
    if (selectedTabIds.length === availableTabs.length) {
      setSelectedTabIds([]);
    } else {
      setSelectedTabIds(availableTabs.map(t => t.id));
    }
  };

  const loadResearchData = async () => {
    try {
      const { data } = await apiClient.getTopics();
      if (data?.topics) {
        setTopics(data.topics.map(normalizeTopic));
      }
    } catch (err) {
      console.error('Failed to load research data:', err);
      setErrorMessage('Could not load research topics. Sync tabs and try again.');
    }
  };

  const handleGenerateSynthesis = async (topic: ResearchTopic) => {
    setIsGenerating(true);
    setSelectedTopic(topic);
    setErrorMessage(null);

    try {
      const { data: raw } = await apiClient.synthesizeTopic(topic.id, {
        journal_format: journalFormat,
        title: topic.name,
        expand_literature: true,
      });
      const newSynthesis = normalizeSynthesis(raw, topic.id);
      setSyntheses(prev => [...prev.filter(s => s.topicId !== topic.id), newSynthesis]);
      setActiveSynthesis(newSynthesis);
      setTopics(prev =>
        prev.map(t => (t.id === topic.id ? { ...t, synthesisStatus: 'completed', lastUpdated: new Date() } : t)),
      );
      setViewMode('synthesis');
    } catch (err: any) {
      console.error('Failed to generate synthesis:', err);
      setErrorMessage(err?.response?.data?.error || 'Synthesis failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateTopicFromSession = async () => {
    setErrorMessage(null);
    if (availableTabs.length > 0 && selectedTabIds.length === 0) {
      setErrorMessage('Please select at least one tab to generate research paper from.');
      return;
    }
    try {
      const { data } = await apiClient.createTopic({
        name: topicName.trim() || 'Current session research',
        session_id: activeSessionId || undefined,
        tab_ids: selectedTabIds.length > 0 ? selectedTabIds : undefined,
        journal_format: journalFormat,
        expand_literature: true,
      });
      const topic = normalizeTopic(data);
      setTopics(prev => [topic, ...prev.filter(t => t.id !== topic.id)]);
      setShowNewTopicModal(false);
    } catch (err: any) {
      setErrorMessage(err?.response?.data?.error || 'Could not create topic — sync browser tabs first.');
    }
  };

  const handleCopyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(id);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleExportSynthesis = async (synthesis: Synthesis, format: string = 'markdown') => {
    setExporting(true);
    try {
      if (synthesis.id) {
        try {
          const blob = await apiClient.exportResearchPaper(synthesis.id, format, synthesis.journalFormat || journalFormat);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `research-${synthesis.id}.${format === 'markdown' ? 'md' : format}`;
          a.click();
          URL.revokeObjectURL(url);
          return;
        } catch (e) {
          console.warn('Soma export failed, falling back to client markdown', e);
        }
      }
      const exportText =
        synthesis.manuscriptMarkdown ||
        `# ${synthesis.title}\n\n## Abstract\n${synthesis.abstract}\n\n${synthesis.sections
          .map(
            section =>
              `## ${section.title}\n\n${section.content}\n\n### Key Insights\n${section.insights.map(i => `- ${i}`).join('\n')}\n`,
          )
          .join('\n')}\n## Key Findings\n${synthesis.keyFindings.map(f => `- ${f}`).join('\n')}\n\n## Recommendations\n${synthesis.recommendations.map(r => `- ${r}`).join('\n')}`;
      const blob = new Blob([exportText], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `synthesis-${synthesis.id}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const allTags = Array.from(new Set(topics.flatMap(t => t.sources.flatMap(s => s.tags))));

  return (
    <PageLayout
      title="Research Paper"
      description="Generate a journal-style manuscript from your tab activity — save as an app or download"
      icon={Brain}
    >
      {/* Header */}
      <div className="border-b border-khiimori-border bg-khiimori-bg-secondary/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleOpenNewTopicModal}
                className="px-4 py-2 bg-khiimori-bg-elevated/80 rounded-lg hover:bg-khiimori-bg-elevated transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Topic
              </button>

              {activeSynthesis && (
                <>
                  <button
                    onClick={() => handleExportSynthesis(activeSynthesis, 'markdown')}
                    disabled={exporting}
                    className="px-4 py-2 bg-khiimori-bg-elevated/80 rounded-lg hover:bg-khiimori-bg-elevated transition-all flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    MD
                  </button>
                  <button
                    onClick={() => handleExportSynthesis(activeSynthesis, 'pdf')}
                    disabled={exporting}
                    className="px-4 py-2 bg-khiimori-bg-elevated/80 rounded-lg hover:bg-khiimori-bg-elevated transition-all flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </button>
                  <button
                    onClick={() => handleExportSynthesis(activeSynthesis, 'docx')}
                    disabled={exporting}
                    className="px-4 py-2 bg-khiimori-bg-elevated/80 rounded-lg hover:bg-khiimori-bg-elevated transition-all flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    DOCX
                  </button>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-khiimori-text-muted">Journal</span>
              <select
                value={journalFormat}
                onChange={(e) => setJournalFormat(e.target.value)}
                className="bg-khiimori-bg-elevated border border-khiimori-border rounded-lg px-3 py-2 text-sm"
              >
                {JOURNAL_FORMATS.map((fmt) => (
                  <option key={fmt} value={fmt}>{fmt.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>

          {errorMessage && (
            <div className="mb-3 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {errorMessage}
            </div>
          )}

          {/* View Mode Tabs */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('topics')}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                viewMode === 'topics' ? 'bg-indigo-600 text-white' : 'bg-khiimori-bg-elevated/50 hover:bg-khiimori-bg-elevated/80'
              }`}
            >
              <Layers className="w-4 h-4" />
              Topics
            </button>
            <button
              onClick={() => setViewMode('synthesis')}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                viewMode === 'synthesis' ? 'bg-indigo-600 text-white' : 'bg-khiimori-bg-elevated/50 hover:bg-khiimori-bg-elevated/80'
              }`}
              disabled={!activeSynthesis}
            >
              <FileText className="w-4 h-4" />
              Synthesis
            </button>
            <button
              onClick={() => setViewMode('sources')}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                viewMode === 'sources' ? 'bg-indigo-600 text-white' : 'bg-khiimori-bg-elevated/50 hover:bg-khiimori-bg-elevated/80'
              }`}
              disabled={!selectedTopic}
            >
              <BookOpen className="w-4 h-4" />
              Sources
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {showNewTopicModal && (
          <div className="mb-8 rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-khiimori-bg-elevated/90 to-khiimori-bg-secondary/90 p-6 shadow-2xl backdrop-blur-xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-khiimori-text-primary flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-400" />
                  Create Topic & Manuscript
                </h3>
                <p className="text-sm text-khiimori-text-muted mt-0.5">
                  Select specific tabs to include as empirical citations and literature sources.
                </p>
              </div>
              <button
                onClick={() => setShowNewTopicModal(false)}
                className="text-khiimori-text-muted hover:text-white p-1 rounded-lg"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-khiimori-text-secondary uppercase tracking-wider mb-1.5">
                  Research Topic Title
                </label>
                <input
                  type="text"
                  value={topicName}
                  onChange={(e) => setTopicName(e.target.value)}
                  placeholder="e.g. Distributed Consensus in Modern Microservices"
                  className="w-full rounded-xl border border-khiimori-border bg-khiimori-bg-primary/70 px-4 py-2.5 text-sm text-white placeholder-khiimori-text-muted focus:border-indigo-500 focus:outline-none"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold text-khiimori-text-secondary uppercase tracking-wider">
                    Select Source Tabs ({selectedTabIds.length} of {availableTabs.length} selected)
                  </label>
                  {availableTabs.length > 0 && (
                    <button
                      type="button"
                      onClick={handleSelectAllTabs}
                      className="text-xs text-indigo-400 hover:text-indigo-300 font-medium transition-colors"
                    >
                      {selectedTabIds.length === availableTabs.length ? 'Deselect All' : 'Select All Tabs'}
                    </button>
                  )}
                </div>

                {loadingTabs ? (
                  <div className="flex items-center justify-center py-8 text-khiimori-text-muted gap-2 text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin text-indigo-400" />
                    Fetching open tabs from workspace...
                  </div>
                ) : availableTabs.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-khiimori-border/60 bg-khiimori-bg-primary/40 p-4 text-center text-sm text-khiimori-text-muted">
                    No open tabs detected in current session. Make sure browser extension is synced.
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto space-y-2 rounded-xl border border-khiimori-border/70 bg-khiimori-bg-primary/50 p-3">
                    {availableTabs.map((tab) => {
                      const isSelected = selectedTabIds.includes(tab.id);
                      return (
                        <div
                          key={tab.id}
                          onClick={() => handleToggleTab(tab.id)}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-indigo-500/50 bg-indigo-500/15'
                              : 'border-transparent bg-khiimori-bg-elevated/30 hover:bg-khiimori-bg-elevated/60'
                          }`}
                        >
                          <button
                            type="button"
                            className="text-indigo-400 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleTab(tab.id);
                            }}
                          >
                            {isSelected ? (
                              <CheckSquare className="w-4 h-4 text-indigo-400" />
                            ) : (
                              <Square className="w-4 h-4 text-khiimori-text-muted" />
                            )}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-khiimori-text-primary truncate">
                              {tab.title || '(Untitled Tab)'}
                            </div>
                            <div className="text-xs text-khiimori-text-muted truncate">
                              {tab.url}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-khiimori-border/60">
              <button
                onClick={() => setShowNewTopicModal(false)}
                className="px-4 py-2 rounded-xl bg-khiimori-bg-elevated/80 hover:bg-khiimori-bg-elevated text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTopicFromSession}
                disabled={availableTabs.length > 0 && selectedTabIds.length === 0}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-sm font-semibold shadow-lg shadow-indigo-500/25 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Topic from Selected ({selectedTabIds.length})
              </button>
            </div>
          </div>
        )}

        {viewMode === 'topics' && topics.length === 0 && (
          <div className="text-center py-16 text-khiimori-text-muted">
            <p className="mb-4">No research topics yet. Sync the browser extension, then create a topic from your tabs.</p>
            <button
              onClick={handleOpenNewTopicModal}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Topic
            </button>
          </div>
        )}

        {viewMode === 'topics' && topics.length > 0 && (
          <TopicsView
            topics={topics}
            onSelectTopic={(topic) => {
              setSelectedTopic(topic);
              const synthesis = syntheses.find(s => s.topicId === topic.id);
              if (synthesis) {
                setActiveSynthesis(synthesis);
                setViewMode('synthesis');
              }
            }}
            onGenerateSynthesis={handleGenerateSynthesis}
            isGenerating={isGenerating}
          />
        )}

        {viewMode === 'synthesis' && activeSynthesis && (
          <SynthesisView
            synthesis={activeSynthesis}
            onCopyText={handleCopyText}
            copiedText={copiedText}
            onToggleSection={(sectionId) => {
              setActiveSynthesis({
                ...activeSynthesis,
                sections: activeSynthesis.sections.map(s =>
                  s.id === sectionId ? { ...s, expanded: !s.expanded } : s
                )
              });
            }}
          />
        )}

        {viewMode === 'sources' && selectedTopic && (
          <SourcesView
            topic={selectedTopic}
            filterTags={filterTags}
            onToggleTag={(tag) => {
              setFilterTags(prev =>
                prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
              );
            }}
            allTags={allTags}
          />
        )}
      </div>
    </PageLayout>
  );
}

// Topics View Component
function TopicsView({ topics, onSelectTopic, onGenerateSynthesis, isGenerating }: any) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {topics.map((topic: ResearchTopic, idx: number) => (
        <motion.div
          key={topic.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.1 }}
          className="glass-card rounded-xl p-6 border border-khiimori-border hover:border-indigo-500/50 transition-all cursor-pointer group"
          onClick={() => onSelectTopic(topic)}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-khiimori-text-primary mb-1 group-hover:text-indigo-400 transition-colors">
                {topic.name}
              </h3>
              <p className="text-xs text-khiimori-text-muted mb-3 line-clamp-2">{topic.description}</p>
            </div>
            <div className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
              topic.synthesisStatus === 'completed' 
                ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'
                : topic.synthesisStatus === 'processing'
                ? 'bg-amber-500/15 border-amber-500/30 text-amber-300'
                : 'bg-indigo-500/10 border-indigo-500/25 text-indigo-300'
            }`}>
              {topic.synthesisStatus}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-khiimori-bg-elevated/40 border border-khiimori-border/50 rounded-xl p-3">
              <div className="text-xl font-bold text-indigo-400">{topic.sources.length}</div>
              <div className="text-[11px] text-khiimori-text-muted font-medium">Selected Tabs</div>
            </div>
            <div className="bg-khiimori-bg-elevated/40 border border-khiimori-border/50 rounded-xl p-3">
              <div className="text-xl font-bold text-purple-400">
                {topic.sources.length > 0 ? Math.round(topic.sources.reduce((acc, s) => acc + s.relevance, 0) / topic.sources.length) : 0}%
              </div>
              <div className="text-[11px] text-khiimori-text-muted font-medium">Avg Relevance</div>
            </div>
            <div className="bg-khiimori-bg-elevated/40 border border-khiimori-border/50 rounded-xl p-3">
              <div className="text-xl font-bold text-pink-400">
                {Math.max(0, Math.round((Date.now() - topic.lastUpdated.getTime()) / (1000 * 60 * 60)))}h
              </div>
              <div className="text-[11px] text-khiimori-text-muted font-medium">Last Updated</div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-khiimori-text-muted mb-4">
            <Clock className="w-3 h-3" />
            Created {topic.createdAt.toLocaleDateString()}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-khiimori-border">
            <div className="flex flex-wrap gap-1">
              {Array.from(new Set(topic.sources.flatMap(s => s.tags))).slice(0, 3).map(tag => (
                <span key={tag} className="px-2 py-1 bg-indigo-600/20 text-indigo-300 rounded text-xs">
                  {tag}
                </span>
              ))}
            </div>
            
            {topic.synthesisStatus !== 'completed' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onGenerateSynthesis(topic);
                }}
                disabled={isGenerating}
                className="px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center gap-2 text-sm disabled:opacity-50"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3 h-3" />
                    Generate Paper
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// Synthesis View Component — displays the single, authoritative Research Manuscript
function SynthesisView({ synthesis, onCopyText, copiedText, onToggleSection }: any) {
  // Prefer the modern journal-style manuscript whenever available
  if (synthesis.manuscriptMarkdown) {
    return (
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl border border-khiimori-border overflow-hidden shadow-2xl"
        >
          <div className="p-4 sm:p-8">
            <ResearchManuscript
              manuscript={synthesis.manuscriptMarkdown}
              paperId={synthesis.id}
              journalFormat={synthesis.journalFormat}
              usedLlm={synthesis.usedLlm}
              title={synthesis.title}
              showDownloads
            />
          </div>
        </motion.div>
      </div>
    );
  }

  // Fallback view only when no generated manuscript markdown exists
  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-xl border border-khiimori-border overflow-hidden"
      >
        {/* Header */}
        <div className="bg-khiimori-bg-secondary/80 p-8 border-b border-khiimori-border">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-xs px-2 py-1 rounded bg-indigo-600/30 text-indigo-200 border border-indigo-500/40">
              Soma Research
            </span>
            {synthesis.journalFormat && (
              <span className="text-xs px-2 py-1 rounded bg-khiimori-bg-elevated text-khiimori-text-secondary border border-khiimori-border uppercase">
                {synthesis.journalFormat}
              </span>
            )}
            <span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
              Template fallback
            </span>
            {(synthesis.sources?.length ?? 0) > 0 && (
              <span className="text-xs px-2 py-1 rounded bg-khiimori-bg-elevated text-khiimori-text-secondary border border-khiimori-border">
                {synthesis.sources.length} sources
              </span>
            )}
          </div>
          <h2 className="text-3xl font-bold text-khiimori-text-primary mb-4">{synthesis.title}</h2>
          
          <div className="flex items-center gap-6 text-sm text-khiimori-text-secondary mb-6">
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {synthesis.createdAt.toLocaleDateString()}
            </span>
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {synthesis.wordCount.toLocaleString()} words
            </span>
            <span className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              {synthesis.sections.length} sections
            </span>
          </div>

          {/* Abstract */}
          <div className="bg-khiimori-bg-secondary/80 rounded-lg p-6 relative">
            <h3 className="text-sm font-semibold text-indigo-300 mb-3">ABSTRACT</h3>
            <p className="text-khiimori-text-secondary leading-relaxed whitespace-pre-wrap">{synthesis.abstract}</p>
            <button
              onClick={() => onCopyText(synthesis.abstract, 'abstract')}
              className="absolute top-4 right-4 p-2 hover:bg-khiimori-bg-elevated/80 rounded-lg transition-all"
            >
              {copiedText === 'abstract' ? (
                <Check className="w-4 h-4 text-success-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Sections */}
        <div className="p-8 space-y-6">
          {synthesis.sections.map((section: SynthesisSection, idx: number) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-khiimori-bg-elevated/50 rounded-lg border border-khiimori-border overflow-hidden"
            >
              <button
                onClick={() => onToggleSection(section.id)}
                className="w-full p-6 flex items-center justify-between hover:bg-khiimori-bg-elevated/50 transition-all"
              >
                <h3 className="text-xl font-semibold text-khiimori-text-primary flex items-center gap-3">
                  {section.expanded ? (
                    <ChevronDown className="w-5 h-5 text-indigo-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-indigo-400" />
                  )}
                  {section.title}
                </h3>
                <span className="text-sm text-khiimori-text-muted">{section.sources.length} sources</span>
              </button>

              <AnimatePresence>
                {section.expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-khiimori-border"
                  >
                    <div className="p-6 space-y-6">
                      <div className="prose prose-invert max-w-none">
                        <p className="text-khiimori-text-secondary leading-relaxed">{section.content}</p>
                      </div>

                      <div className="bg-indigo-500/10 rounded-lg p-4 border border-indigo-500/20">
                        <h4 className="text-sm font-semibold text-indigo-300 mb-3 flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Key Insights
                        </h4>
                        <ul className="space-y-2">
                          {section.insights.map((insight, i) => (
                            <li key={i} className="text-sm text-khiimori-text-secondary flex items-start gap-2">
                              <Target className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                              {insight}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}

          {/* Key Findings */}
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-lg p-6 border border-green-500/20">
            <h3 className="text-lg font-semibold text-success-300 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Key Findings
            </h3>
            <ul className="space-y-3">
              {synthesis.keyFindings.map((finding: string, idx: number) => (
                <li key={idx} className="text-khiimori-text-secondary flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-400 mt-0.5 flex-shrink-0" />
                  {finding}
                </li>
              ))}
            </ul>
          </div>

          {/* Recommendations */}
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg p-6 border border-purple-500/20">
            <h3 className="text-lg font-semibold text-purple-300 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Recommendations
            </h3>
            <ul className="space-y-3">
              {synthesis.recommendations.map((recommendation: string, idx: number) => (
                <li key={idx} className="text-khiimori-text-secondary flex items-start gap-3">
                  <Target className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                  {recommendation}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Sources View Component
function SourcesView({ topic, filterTags, onToggleTag, allTags }: any) {
  const filteredSources = topic.sources.filter((source: Source) =>
    filterTags.length === 0 || filterTags.some((tag: string) => source.tags.includes(tag))
  );

  return (
    <div className="space-y-6">
      {/* Tag Filters */}
      <div className="glass-card rounded-xl p-4 border border-khiimori-border">
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-khiimori-text-muted" />
          {allTags.map((tag: string) => (
            <button
              key={tag}
              onClick={() => onToggleTag(tag)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                filterTags.includes(tag)
                  ? 'bg-indigo-600 text-white'
                  : 'bg-khiimori-bg-elevated/80 hover:bg-khiimori-bg-elevated'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Sources List */}
      <div className="space-y-4">
        {filteredSources.map((source: Source, idx: number) => (
          <motion.div
            key={source.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="glass-card rounded-xl p-6 border border-khiimori-border hover:border-indigo-500/50 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Globe className="w-5 h-5 text-indigo-400" />
                  <h3 className="text-lg font-semibold text-khiimori-text-primary">{source.title}</h3>
                </div>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-2"
                >
                  {source.domain}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="text-2xl font-bold text-indigo-400">{source.relevance}%</div>
                  <div className="text-xs text-khiimori-text-muted">Relevance</div>
                </div>
              </div>
            </div>

            {/* Excerpts */}
            <div className="space-y-2 mb-4">
              {source.excerpts.map((excerpt, i) => (
                <div key={i} className="bg-khiimori-bg-elevated/50 rounded-lg p-3 border-l-2 border-indigo-500">
                  <p className="text-sm text-khiimori-text-secondary italic">"{excerpt}"</p>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-khiimori-border">
              <div className="flex flex-wrap gap-2">
                {source.tags.map(tag => (
                  <span key={tag} className="px-2 py-1 bg-indigo-600/20 text-indigo-300 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>
              <span className="text-xs text-khiimori-text-muted flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {source.visitedAt.toLocaleDateString()}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
